import clsx from 'clsx';

import type { ComponentClass, ElementType, JSX, ReactNode } from 'react';

import type { StaticImageData } from 'next/image';

import { type CarbonIconType } from '@carbon/icons-react';
import { Tag } from '@carbon/react';

import { HorizontalSeparator } from '@/components/HorizontalSeparator';
import { ImageWithFallBack } from '@/components/ImageWithFallBack';
import type { Props as InteractiveLinkProps } from '@/components/InteractiveLink';
import { InteractiveLink } from '@/components/InteractiveLink';
import type { TagType } from '@/types/TagType';

import styles from './content-card.module.scss';

const MAX_TITLE_LENGTH = 66;
const MAX_COPY_LENGTH = 164;
const ELLIPSIS = '...';
const ELLIPSIS_LENGTH = ELLIPSIS.length;

export type LabelWithIcon = {
  label: string | undefined;
  icon: ReactNode;
};

type HeadingLevel = 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

export type ContentCardProps = {
  className?: string;
  as?: ElementType;
  copy?: string;
  eyebrow?: string;
  hasGradientIcon?: boolean;
  headingLevel?: HeadingLevel;
  icon?: CarbonIconType | ComponentClass<{ size: number; className: string }>;
  image?: {
    src: StaticImageData;
    alt: string;
    desaturate?: boolean;
    priority?: boolean;
    width?: number;
    height?: number;
    fallbackSrc?: StaticImageData;
    loading?: 'eager' | 'lazy';
  } | null;
  labelsWithIcon?: LabelWithIcon[];
  link?: InteractiveLinkProps;
  maxTitleLength?: number | null;
  secondaryLink?: InteractiveLinkProps | null;
  tags?: { text: string; type: TagType }[];
  title?: string | undefined;
  titleHighlight?: string; // New optional prop for title highlighting
};

export function ContentCard({
  className,
  as,
  copy,
  eyebrow,
  hasGradientIcon = false,
  // TODO: Review the correct heading level in the components where this is used
  headingLevel = 'h3',
  icon: Icon,
  image,
  labelsWithIcon,
  link,
  secondaryLink,
  tags,
  title,
  titleHighlight,
}: ContentCardProps) {
  const BaseComponent: ElementType = as || 'div';
  const iconClassName = `${styles['content-card__heading__icon']} ${
    hasGradientIcon && styles['content-card__heading__icon--gradient']
  }`;

  const validLevels: HeadingLevel[] = ['h2', 'h3', 'h4', 'h5', 'h6'];
  const safeLevel = validLevels.includes(headingLevel) ? headingLevel : 'h3';
  const Heading = safeLevel as keyof JSX.IntrinsicElements;

  const cardTitle = truncTextIfNeeded(title || '', MAX_TITLE_LENGTH);

  const formattedTitle = (
    <Heading className={clsx(styles['content-card__heading__title'], 'text-h5')} title={title}>
      {titleHighlight
        ? cardTitle.split(titleHighlight).map((textSegment, index, array) =>
            index < array.length - 1 ? (
              <span key={index}>
                {textSegment}
                <span className="text-alt">{titleHighlight}</span>
              </span>
            ) : (
              textSegment
            )
          )
        : cardTitle}
    </Heading>
  );

  return (
    <BaseComponent className={clsx(styles['content-card'], className)}>
      <div>
        {image && (
          <ImageWithFallBack
            height={400}
            width={400}
            priority={image.priority}
            src={image.src}
            alt={image.alt}
            className={`${styles['content-card__image']} ${
              image.desaturate ? styles['content-card__image--desaturate'] : null
            }`}
            fallbackSrc={image.fallbackSrc}
            loading={image.loading}
          />
        )}
        <HorizontalSeparator className={styles['content-card__separator']} />
        <div className={styles['content-card__heading']}>
          {eyebrow && <p className={styles['content-card__heading__eyebrow']}>{eyebrow}</p>}
          {Icon && <Icon size={32} className={iconClassName} />}
          {title && formattedTitle}
          {link && <InteractiveLink {...link} hasIcon />}
        </div>
        <div className={styles['content-card__content']}>
          {copy && (
            <p className={styles['content-card__content__copy']} title={copy}>
              {truncTextIfNeeded(copy, MAX_COPY_LENGTH)}
            </p>
          )}
          {labelsWithIcon && (
            <div className={styles['content-card__content__label-with-icon']}>
              {labelsWithIcon.map((item) => (
                <div
                  key={item.label}
                  className={styles['content-card__content__label-with-icon__item']}
                >
                  {item.icon}
                  <span className={styles['content-card__content__label-with-icon__item-label']}>
                    {item.label}
                  </span>
                </div>
              ))}
            </div>
          )}
          {secondaryLink && (
            <InteractiveLink
              {...secondaryLink}
              hasIcon
              variant="caption"
              className={styles['content-card__content__secondary-link']}
            />
          )}
          {tags && (
            <div className={styles['content-card__content__tag-group']}>
              {tags.map((tag) => (
                <Tag key={tag.text} id={tag.text} filter={false} title={tag.text} type={tag.type}>
                  {tag.text}
                </Tag>
              ))}
            </div>
          )}
          {/* This svg is used as a reference for the icon gradient fill in the scss. This technique is mentioned here: https://developer.mozilla.org/en-US/docs/Web/SVG/Tutorial/Gradients */}
          <svg
            style={{ width: 0, height: 0, position: 'absolute' }}
            aria-hidden="true"
            focusable="false"
          >
            <linearGradient id="icon-gradient" x2="1" y2="1">
              <stop offset="0%" stopColor="#0f62fe" />
              <stop offset="100%" stopColor="#9B5CFF" />
            </linearGradient>
          </svg>
        </div>
      </div>
    </BaseComponent>
  );
}

function truncTextIfNeeded(str: string, maxLength: number) {
  if (str.length <= maxLength) {
    return str;
  }

  return `${str.substring(0, maxLength - ELLIPSIS_LENGTH)} ${ELLIPSIS}`;
}
